import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Download, ClipboardEdit, FileText } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTASection } from "@/components/site/CTASection";
import { Reveal } from "@/components/motion/Reveal";
import { QuestionnaireForm } from "@/components/site/QuestionnaireForm";

import menopause from "@/data/questionnaires/menopause.json";
import fertility from "@/data/questionnaires/fertility.json";
import general from "@/data/questionnaires/general.json";
import comprehensive from "@/data/questionnaires/comprehensive.json";

const FORMS = { menopause, fertility, general, comprehensive };

// Current briefing: keep the general intake form visible and add the
// Kinderwunsch packet as a printable/fillable booklet for fertility patients.
const VISIBLE_FORMS = { general, fertility };
const DOWNLOAD_LANGS = ["de", "en", "ar"];

const PDF_STEMS = {
  menopause: "HAYAT_Anamnesebogen_Wechseljahre",
  fertility: "HAYAT_Anamnesebogen_Kinderwunsch",
  general: "HAYAT_Anamnesebogen_Allgemein",
  comprehensive: "HAYAT_Anamnesebogen_Umfassend",
};

const FORM_LANG = (lng) => (["de", "en", "ar"].includes(lng) ? lng : "en");

const MATERNITY_PASS = {
  de: {
    title: "Mutterpass zum Ausdrucken",
    text: "Offizielle G-BA-Vorlage in Mutterpass-Größe, vorbereitet als druckbare Datei für die Ausgabe an schwangere Patientinnen.",
    downloads: [
      { lang: "DE", file: "HAYAT_Mutterpass_Druck_DE.pdf" },
      { lang: "EN", file: "HAYAT_Mutterpass_Druck_EN.pdf" },
    ],
  },
  en: {
    title: "Printable maternity record",
    text: "Official G-BA template in maternity-record size, prepared as a printable file for pregnant patients.",
    downloads: [
      { lang: "DE", file: "HAYAT_Mutterpass_Druck_DE.pdf" },
      { lang: "EN", file: "HAYAT_Mutterpass_Druck_EN.pdf" },
    ],
  },
  ar: {
    title: "دفتر متابعة الحمل للطباعة",
    text: "نموذج G-BA الرسمي بحجم دفتر متابعة الحمل، مجهز كملف للطباعة للمريضات الحوامل.",
    downloads: [
      { lang: "DE", file: "HAYAT_Mutterpass_Druck_DE.pdf" },
      { lang: "EN", file: "HAYAT_Mutterpass_Druck_EN.pdf" },
    ],
  },
};

export const FormulareHub = () => {
  const { t, i18n } = useTranslation();
  const lang = FORM_LANG(i18n.language?.split("-")[0]);

  return (
    <div data-testid="formulare-hub">
      <PageHero kicker={t("questionnaire.hub.kicker")} title={t("questionnaire.hub.title")} text={t("questionnaire.hub.text")} />
      <section className="bg-warm-ivory">
        <div className="mx-auto max-w-2xl px-5 pb-24 lg:px-10">
          <div className="grid gap-6">
            {Object.values(VISIBLE_FORMS).map((form, i) => (
              <Reveal key={form.slug} delay={(i % 2) * 0.06}>
                <div className="flex h-full flex-col rounded-3xl border border-border bg-sand p-7" data-testid={`formulare-card-${form.slug}`}>
                  <h3 className="font-serif text-2xl leading-snug text-deep-ink">{form.title[lang]}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-deep-ink/70">{form.intro[lang]}</p>
                  <Link
                    to={`/formulare/${form.slug}`}
                    data-testid={`formulare-fill-${form.slug}`}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-deep-ink px-6 py-3 text-sm font-medium text-warm-ivory transition-colors hover:bg-champagne-gold hover:text-deep-ink"
                  >
                    <ClipboardEdit className="h-4 w-4" />
                    {t("questionnaire.hub.fillOnline")}
                  </Link>
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-4">
                    {DOWNLOAD_LANGS.map((l) => (
                      <a
                        key={l}
                        href={`/downloads/${PDF_STEMS[form.slug]}_${l.toUpperCase()}.pdf`}
                        download
                        data-testid={`formulare-pdf-${form.slug}-${l}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-champagne-gold hover:underline"
                      >
                        <Download className="h-3.5 w-3.5" />
                        PDF ({l.toUpperCase()})
                      </a>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.12}>
              <div className="flex h-full flex-col rounded-3xl border border-border bg-sand p-7" data-testid="formulare-card-maternity-pass">
                <h3 className="font-serif text-2xl leading-snug text-deep-ink">{MATERNITY_PASS[lang].title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-deep-ink/70">{MATERNITY_PASS[lang].text}</p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
                  {MATERNITY_PASS[lang].downloads.map((download) => (
                    <a
                      key={download.file}
                      href={`/downloads/${download.file}`}
                      download
                      data-testid={`formulare-pdf-mutterpass-${download.lang.toLowerCase()}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-champagne-gold hover:underline"
                    >
                      <Download className="h-3.5 w-3.5" />
                      PDF ({download.lang})
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
};

export const FormulareDetail = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = FORM_LANG(i18n.language?.split("-")[0]);
  const form = FORMS[slug];

  if (!form) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center">
        <p className="text-deep-ink/70">{t("questionnaire.hub.notFound")}</p>
        <Link to="/formulare" className="mt-4 inline-flex items-center gap-2 text-champagne-gold">
          <ArrowLeft className="h-4 w-4" />{t("questionnaire.hub.backToForms")}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`formulare-detail-${slug}`}>
      <PageHero kicker={t("questionnaire.hub.kicker")} title={form.title[lang]} text={form.intro[lang]} />
      <section className="bg-warm-ivory">
        <div className="mx-auto max-w-5xl px-5 pb-24 pt-4 lg:px-10">
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <Link to="/formulare" className="inline-flex items-center gap-2 text-sm font-medium text-champagne-gold">
              <ArrowLeft className="rtl-flip h-4 w-4" />{t("questionnaire.hub.backToForms")}
            </Link>
            <a
              href={`/downloads/${PDF_STEMS[slug]}_${lang.toUpperCase()}.pdf`}
              download
              data-testid={`formulare-detail-pdf-${slug}`}
              className="inline-flex items-center gap-2 text-sm font-medium text-deep-ink/70 hover:text-champagne-gold"
            >
              <FileText className="h-4 w-4" />{t("questionnaire.hub.downloadInstead")}
            </a>
          </div>
          <QuestionnaireForm data={form} />
        </div>
      </section>
      <CTASection />
    </div>
  );
};
